/**
 * scrubPII — allowlist-based PII filter.
 *
 * Everything NOT on ALLOWED_AGGREGATE_FIELDS is dropped.
 * Allowlist is safer than blocklist — new Firestore fields added in future
 * are blocked by default until explicitly reviewed and allowed.
 *
 * Returns a flat object of safe aggregate metrics only.
 * Attaches _scrubAudit for internal logging — never sent to AI or server.
 */

const ALLOWED_AGGREGATE_FIELDS = new Set([
  'repName', 'repId', 'reportingMonth', 'reportingYear',
  'unitsSold', 'monthlyGoal', 'goalAttainment',
  'commissionMTD', 'totalGrossMTD', 'avgGrossPerUnit',
  'callsMade', 'textsSent', 'emailsSent', 'totalOutreach',
  'leadsContacted', 'leadsClosed', 'leadConversionRate',
  'apptSet', 'apptShown', 'apptClosed',
  'apptSetRate', 'apptShowRate', 'apptCloseRate',
  'sellingDaysElapsed', 'sellingDaysRemaining', 'daysInMonth',
  'unitsFirstWeek', 'unitsSecondWeek', 'unitsThirdWeek', 'unitsLastWeek',
  'zeroDays', 'firstTouchRate', 'secondTouchRate',
  'paceToGoal', 'currentPacePerDay', 'avgDailyActivity',
  'incomeGoal',
]);

const EXPLICITLY_BLOCKED = new Set([
  'leadName', 'customerName', 'firstName', 'lastName', 'fullName', 'name',
  'phone', 'phoneNumber', 'mobile', 'cell', 'telephone',
  'email', 'emailAddress',
  'address', 'streetAddress', 'street', 'city', 'state', 'zip',
  'zipCode', 'postalCode',
  'vin', 'stockNumber', 'stock',
  'downPayment', 'downPaymentAmount', 'down',
  'paymentTerms', 'monthlyPayment', 'payment',
  'tradeIn', 'tradeValue', 'trade',
  'notes', 'leadNotes', 'customerNotes', 'dealNotes', 'memo', 'comment',
  'ssn', 'socialSecurity', 'dob', 'dateOfBirth', 'birthdate',
  'creditScore', 'creditTier', 'credit',
  'dealGross', 'deals', 'leads', 'contacts', 'appointments', 'customers',
]);

export function scrubPII(rawData) {
  if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) {
    throw new Error('scrubPII: input must be a non-null, non-array object');
  }

  const scrubbed = {};
  const blockedFields = [];
  const allowedFields = [];

  for (const [key, value] of Object.entries(rawData)) {
    if (EXPLICITLY_BLOCKED.has(key)) {
      blockedFields.push({ field: key, reason: 'explicitly_blocked' });
      continue;
    }
    if (!ALLOWED_AGGREGATE_FIELDS.has(key)) {
      blockedFields.push({ field: key, reason: 'not_in_allowlist' });
      continue;
    }
    if (Array.isArray(value)) {
      blockedFields.push({ field: key, reason: 'array_blocked' });
      continue;
    }
    if (typeof value === 'object' && value !== null) {
      blockedFields.push({ field: key, reason: 'object_blocked' });
      continue;
    }
    if (typeof value === 'string' && stringContainsPII(value)) {
      blockedFields.push({ field: key, reason: 'string_pii_heuristic' });
      console.warn(`[scrubPII] Field "${key}" failed PII string check — blocked.`);
      continue;
    }
    scrubbed[key] = value;
    allowedFields.push(key);
  }

  scrubbed._scrubAudit = {
    timestamp: new Date().toISOString(),
    fieldsAllowed: allowedFields.length,
    fieldsBlocked: blockedFields.length,
    blockedFields,
    scrubVersion: '1.0',
  };

  return scrubbed;
}

function stringContainsPII(str) {
  if (str.length > 200) return true;
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  const phonePattern = /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/;
  const ssnPattern   = /\d{3}-\d{2}-\d{4}/;
  return emailPattern.test(str) || phonePattern.test(str) || ssnPattern.test(str);
}

/**
 * assertScrubbed — runtime tripwire.
 *
 * Call at the top of any function that feeds data into the insight
 * pipeline or builds an AI prompt. Throws immediately if scrubPII()
 * was skipped, making the bypass impossible to miss.
 */
export function assertScrubbed(data, callerName) {
  if (!data?._scrubAudit) {
    throw new Error(
      `[Privacy Violation] ${callerName} received data that has not passed ` +
      `through scrubPII(). All data must be scrubbed before entering the pipeline.`
    );
  }
}
